import React, { useState, useRef, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { Phone, ShieldCheck, Loader2 } from "lucide-react";

export default function PhoneVerifyDialog({ open, onOpenChange, onVerified }) {
  const [step, setStep] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [mergeWarning, setMergeWarning] = useState(null);
  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (open) { setStep("phone"); setPhone(""); setOtp(["","","","","",""]); setError(""); setCountdown(0); setMergeWarning(null); }
  }, [open]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleSendOTP = async () => {
    if (!phone || phone.replace(/\D/g, "").length < 9) { setError("SĐT không hợp lệ"); return; }
    setError(""); setSending(true); setMergeWarning(null);
    try {
      const { data } = await api.post("/auth/send-otp", { phone });
      if (data.will_merge && data.merge_info) {
        setMergeWarning(data.merge_info.message);
      }
      setStep("otp");
      setCountdown(60);
      setTimeout(() => otpRefs[0].current?.focus(), 200);
    } catch (err) {
      setError(err.response?.data?.detail || "Lỗi gửi OTP");
    } finally { setSending(false); }
  };

  const handleOtpChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    setError("");
    if (val && idx < 5) otpRefs[idx + 1].current?.focus();
    if (idx === 5 && val) {
      // Auto verify
      handleVerify(next.join(""));
    }
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) otpRefs[idx - 1].current?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(""));
      handleVerify(pasted);
    }
  };

  const handleVerify = async (code) => {
    setError(""); setSending(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { otp: code });
      if (data.merged) {
        alert(`Đã gộp tài khoản "${data.merged_account}" vào tài khoản của bạn. Toàn bộ lịch sử đặt sân và chi tiêu đã được hợp nhất.`);
      }
      if (onVerified) onVerified(data.user);
      onOpenChange(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Mã OTP không đúng");
      setOtp(["","","","","",""]); 
      setTimeout(() => otpRefs[0].current?.focus(), 150);
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-sm" data-testid="phone-verify-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2">
            {step === "phone" ? <Phone className="w-5 h-5 text-teal-400" /> : <ShieldCheck className="w-5 h-5 text-lime-400" />}
            {step === "phone" ? "Xác nhận số điện thoại" : "Nhập mã OTP"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {step === "phone"
              ? "Nhập SĐT để nhận mã xác nhận qua tin nhắn Zalo"
              : `Nhập mã OTP nhận qua tin nhắn Zalo vào số ${phone}. Hiệu lực 5 phút.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {error && <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2" data-testid="otp-error">{error}</div>}
          {mergeWarning && (
            <div className="text-xs text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-lg px-3 py-2.5" data-testid="merge-warning">
              <b className="text-cyan-400">Lưu ý gộp tài khoản:</b> {mergeWarning}
            </div>
          )}

          {step === "phone" ? (
            <>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input type="tel" value={phone} onChange={(e) => { setPhone(e.target.value); setError(""); }}
                  placeholder="0901234567" className="pl-10 bg-[#0f172a] border-[#334155] text-slate-50 h-12 text-lg" autoFocus
                  data-testid="phone-input" />
              </div>
              <Button onClick={handleSendOTP} disabled={sending}
                className="w-full bg-teal-500 hover:bg-teal-600 text-[#0f172a] font-semibold h-11" data-testid="send-otp-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gửi mã OTP qua Zalo"}
              </Button>
            </>
          ) : (
            <>
              <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
                {otp.map((d, i) => (
                  <input key={i} ref={otpRefs[i]} type="text" inputMode="numeric" maxLength={1} value={d}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className={`w-11 h-14 text-center text-xl font-bold rounded-lg border-2 bg-[#0f172a] text-white outline-none transition-colors ${d ? "border-lime-500" : "border-[#334155] focus:border-teal-500"}`}
                    data-testid={`otp-input-${i}`} />
                ))}
              </div>
              <div className="flex justify-between items-center">
                <button onClick={() => setStep("phone")} className="text-xs text-slate-500 hover:text-slate-300">Đổi SĐT</button>
                {countdown > 0 ? (
                  <span className="text-xs text-slate-500">Gửi lại sau {countdown}s</span>
                ) : (
                  <button onClick={handleSendOTP} disabled={sending} className="text-xs text-teal-400 hover:text-teal-300">Gửi lại OTP</button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
